import type { HTTPService } from "@soft-db/http";

export type Role = "sys_admin" | "admin" | "editor" | "viewer";
export const Role: Record<Role, Role> = {
	sys_admin: "sys_admin",
	admin: "admin",
	editor: "editor",
	viewer: "viewer",
};

export type Features = {
	dashboard: boolean;
	integrity: boolean;
	report: boolean;
	audit: boolean;
};

export type UserAuth =
	| {
			type: "email";
			email: string;
			verified: boolean;
	  }
	| {
			type: "username";
			username: string;
	  }
	| {
			type: "public";
	  };

export type UserAuthType = UserAuth["type"];

export type ScopeType = "distributor" | "space" | "none";

export type UserScope = {
	type: ScopeType;
	id: string;
};

export type UpdateUserPassword = {
	password: string;
	confirm: string;
};

export type User = {
	id: string;
	name: string;
	preferredLang: string;
	features: Features;
	avatarUrl: string;
	role: Role;
	scope: UserScope;
	archived: boolean;
};

export type UserSummary = User & {
	authType: UserAuthType;
};

export type UserWithDetails = User & {
	auth: UserAuth;
	scopeFeatures: Features;
};

export type UpdateUser = Pick<User, "name" | "preferredLang" | "features">;

export type CreateUserAuth =
	| {
			type: "email";
			email: string;
	  }
	| {
			type: "username";
			username: string;
			password: string;
	  };

export type CreateUser = Pick<User, "name" | "role" | "preferredLang" | "features"> & {
	auth: CreateUserAuth;
};

export class UserApi {
	constructor(private _http: HTTPService) {}

	async fetchContext() {
		return await this._http.get<UserSummary[]>(`users`);
	}

	async fetch(id: string) {
		return await this._http.get<UserWithDetails>(`users/${id}`);
	}

	async tryFetch(id: string) {
		const result = await this._http.withResponseProxy(
			this._http.get<UserWithDetails>(`users/${id}`, { ignoreError: true })
		);

		if (result.status === "FAILED") {
			return null;
		}

		return result.data;
	}

	async update(id: string, difference: UpdateUser) {
		return await this._http.patch<UserWithDetails>(`users/${id}`, difference);
	}

	async archive(id: string) {
		return await this._http.delete(`users/${id}`);
	}

	async restore(id: string) {
		return await this._http.post<UserWithDetails>(`users/${id}/restore`);
	}

	async resendActivation(id: string) {
		return await this._http.post(`users/${id}/resend-activation`);
	}

	async updateAvatar(id: string, file: File) {
		return await this._http.postFile<UserWithDetails>(`users/${id}/avatar`, "file", file);
	}

	async deleteAvatar(id: string) {
		return await this._http.delete<UserWithDetails>(`users/${id}/avatar`);
	}

	async updateEmail(id: string, email: string) {
		return await this._http.put<UserWithDetails>(`users/${id}/email`, { email });
	}

	async updateRole(id: string, role: Role) {
		return await this._http.put<UserWithDetails>(`users/${id}/role`, { role });
	}

	async fetchDistUsers(id: string) {
		return await this._http.get<UserSummary[]>(`distributors/${id}/users`);
	}

	async createDistUser(id: string, user: CreateUser) {
		return await this._http.post<UserWithDetails>(`distributors/${id}/users`, user);
	}

	async fetchSpaceUsers(id: string) {
		return await this._http.get<UserSummary[]>(`spaces/${id}/users`);
	}

	async createSpaceUser(id: string, user: CreateUser) {
		return await this._http.post<UserWithDetails>(`spaces/${id}/users`, user);
	}
}
